import { ContainerModel } from './ContainerModel.svelte.js';
import ImageEntryModel from './ImageEntryModel.svelte.js';

import {
  extract_text_teaser_from_container,
  extract_images_from_container,
  extract_title_from_container,
  extract_preamble_from_container,
  nanoid,
} from '#lib/util.js';

export default class PageEntryModel {
  // system-native props
  entry_id = $state();
  type = $state();
  slug = $state();
  created_at = $state();
  updated_at = $state();
  user_id = $state();
  visibility = $state('public');
  preview_title = $state('');
  preview_preamble = $state('');
  preview_description = $state('');
  preview_images_multiref = $state([]);
  preview_images = $state([]);
  tag_keys = $state([]);
  exclude_from_search = $state(false); // hidden from site search, sitemap and search engines
  // type-specific custom props
  body = $state([]);

  // derive preview from the current entry
  preview = $derived(this.to_preview());

  constructor({
    entry_id,
    type, 
    slug,
    created_at,
    updated_at,
    user_id,
    visibility,
    preview_title,
    preview_preamble,
    preview_description,
    preview_images_multiref = [],
    preview_images = [], // computed from preview_images_multiref
    tag_keys = [],
    exclude_from_search,
    body = []
  } = {}) {
    // system-native props
    this.entry_id = entry_id,
    this.type = type;
    this.slug = slug;
    this.created_at = created_at;
    this.updated_at = updated_at;
    this.user_id = user_id;
    this.visibility = visibility ?? 'public';
    this.preview_title = preview_title ?? '';
    this.preview_preamble = preview_preamble ?? '';
    this.preview_description = preview_description ?? '';
    this.preview_images_multiref = preview_images_multiref ?? [];
    this.preview_images = preview_images?.map(i => new ImageEntryModel(i)) ?? [];
    this.tag_keys = tag_keys ?? [];
    this.exclude_from_search = exclude_from_search ?? false;

    // type-specific custom props
    this.body = new ContainerModel({
      blocks: body?.length > 0 ? body : [{ block_id: nanoid(), type: 'story', title_level: 1, description: '' }], // default to empty story block if no body is provided
      block_types: ['story'],
      default_block_type: 'story'
    }, this);
  }

  to_preview() {
    return {
      entry_id: this.entry_id,
      type: this.type,
      slug: this.slug,
      preamble: this.preview_preamble || extract_preamble_from_container(this.body),
      title: this.preview_title || extract_title_from_container(this.body),
      description: this.preview_description || extract_text_teaser_from_container(this.body),
      // NOTE: since we use raw objects on server side, preview_images might be undefined
      images: this.preview_images?.length > 0 ? this.preview_images.map(i => i.to_json()) : extract_images_from_container(this.body).slice(0, 3),
      tag_keys: this.tag_keys,
      created_at: this.created_at,
      updated_at: this.updated_at,
      visibility: this.visibility,
    };
  }

  /** Dispose all child effects (delegates to body container) */
  dispose() {
    this.body?.dispose?.();
  }

  to_json() {
    return {
      entry_id: this.entry_id,
      type: this.type,
      visibility: this.visibility,
      preview_title: this.preview_title,
      preview_preamble: this.preview_preamble,
      preview_description: this.preview_description,
      preview_images_multiref: this.preview_images_multiref,
      tag_keys: this.tag_keys,
      exclude_from_search: this.exclude_from_search || undefined,
      body: this.body.to_json(),
    };
  }

  toJSON() {
    return this.to_json();
  }
}
